import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/Card'
import { PermissionGate } from '@/components/PermissionGate'
import { userManagementService, type UserWithPermissions } from '@/services/userManagement'
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Eye,
  UserCheck,
  UserX,
  Shield,
  FileText,
  User,
  Calendar,
  Utensils
} from 'lucide-react'
import toast from 'react-hot-toast'

export function UsersListPage() {
  const [users, setUsers] = useState<UserWithPermissions[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')

  // Carregar usuários
  const loadUsers = async () => {
    try {
      setLoading(true)
      const response = await userManagementService.getUsers()
      if (response.success) {
        setUsers(response.data)
      } else {
        toast.error(response.message || 'Erro ao carregar usuários')
      }
    } catch (error) {
      toast.error('Erro ao carregar usuários')
      console.error('Erro:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadUsers()
  }, [])

  // Deletar usuário
  const handleDeleteUser = async (userId: number, userName: string) => {
    if (!confirm(`Tem certeza que deseja remover o usuário "${userName}"?`)) {
      return
    }

    try {
      const response = await userManagementService.deleteUser(userId)
      if (response.success) {
        toast.success(response.message || 'Usuário removido com sucesso')
        loadUsers()
      } else {
        toast.error(response.message || 'Erro ao remover usuário')
      }
    } catch (error) {
      toast.error('Erro ao remover usuário')
      console.error('Erro:', error)
    }
  }

  // Alternar status ativo/inativo
  const handleToggleUserStatus = async (userId: number, isActive: boolean) => {
    try {
      const response = await userManagementService.updateUser(userId, { isActive: !isActive })
      if (response.success) {
        toast.success(`Usuário ${!isActive ? 'ativado' : 'desativado'} com sucesso`)
        loadUsers()
      } else {
        toast.error(response.message || 'Erro ao atualizar usuário')
      }
    } catch (error) {
      toast.error('Erro ao atualizar usuário')
      console.error('Erro:', error)
    }
  }

  // Filtrar usuários
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.login.toLowerCase().includes(searchTerm.toLowerCase())
    
    const userRoles = Array.isArray(user.role) ? user.role : [user.role]
    const matchesRole = roleFilter === 'all' || userRoles.includes(roleFilter as any)

    return matchesSearch && matchesRole
  })

  // Obter ícone do role
  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'administrador':
        return <Shield className="w-4 h-4" />
      case 'vistoria':
        return <FileText className="w-4 h-4" />
      case 'calendario':
        return <Calendar className="w-4 h-4" />
      case 'cardapio':
        return <Utensils className="w-4 h-4" />
      case 'usuario_basico':
        return <User className="w-4 h-4" />
      default:
        return <User className="w-4 h-4" />
    }
  }

  // Obter cor do role
  const getRoleColor = (role: string) => {
    switch (role) {
      case 'administrador':
        return 'bg-red-100 text-red-800'
      case 'vistoria':
        return 'bg-blue-100 text-blue-800'
      case 'calendario':
        return 'bg-green-100 text-green-800'
      case 'cardapio':
        return 'bg-orange-100 text-orange-800'
      case 'usuario_basico':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Estatísticas
  const stats = {
    total: users.length,
    ativos: users.filter(u => u.isActive).length,
    administradores: users.filter(u => {
      const roles = Array.isArray(u.role) ? u.role : [u.role]
      return roles.includes('administrador')
    }).length,
    vistorias: users.filter(u => {
      const roles = Array.isArray(u.role) ? u.role : [u.role]
      return roles.includes('vistoria')
    }).length,
    calendarios: users.filter(u => {
      const roles = Array.isArray(u.role) ? u.role : [u.role]
      return roles.includes('calendario')
    }).length,
    cardapios: users.filter(u => {
      const roles = Array.isArray(u.role) ? u.role : [u.role]
      return roles.includes('cardapio')
    }).length
  }

  return (
    <div className="w-full">
      {/* Cabeçalho */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Usuários</h1>
            <p className="text-gray-600">Gerencie usuários e permissões do sistema</p>
          </div>
          <PermissionGate permission="user_create">
            <Link
              to="/usuarios/novo"
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Plus className="w-4 h-4" />
              <span>Novo Usuário</span>
            </Link>
          </PermissionGate>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mb-8">
        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-blue-50 mr-4">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total de Usuários</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-50 mr-4">
              <UserCheck className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Usuários Ativos</p>
              <p className="text-2xl font-bold text-gray-900">{stats.ativos}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-red-50 mr-4">
              <Shield className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Administradores</p>
              <p className="text-2xl font-bold text-gray-900">{stats.administradores}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-purple-50 mr-4">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Vistorias</p>
              <p className="text-2xl font-bold text-gray-900">{stats.vistorias}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-green-50 mr-4">
              <Calendar className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Calendários</p>
              <p className="text-2xl font-bold text-gray-900">{stats.calendarios}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-lg bg-orange-50 mr-4">
              <Utensils className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Cardápios</p>
              <p className="text-2xl font-bold text-gray-900">{stats.cardapios}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filtros */}
      <Card className="p-6 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar usuário
            </label>
            <input
              type="text"
              placeholder="Digite nome ou login..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="md:w-64">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Filtrar por role
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">Todos os roles</option>
              <option value="administrador">Administrador</option>
              <option value="vistoria">Vistoria</option>
              <option value="calendario">Calendário</option>
              <option value="cardapio">Cardápio</option>
              <option value="usuario_basico">Usuário Básico</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Lista de usuários */}
      <Card className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Carregando usuários...</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-8">
            <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nenhum usuário encontrado</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Usuário</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Role</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Status</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Criado em</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-4 px-4">
                      <div>
                        <p className="font-medium text-gray-900">{user.nome}</p>
                        <p className="text-sm text-gray-500">@{user.login}</p>
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-wrap gap-1">
                        {(Array.isArray(user.role) ? user.role : [user.role]).map((role, index) => (
                          <span key={index} className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(role)}`}>
                            <span className="mr-1">{getRoleIcon(role)}</span>
                            {role.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {user.isActive ? (
                          <>
                            <UserCheck className="w-3 h-3 mr-1" />
                            Ativo
                          </>
                        ) : (
                          <>
                            <UserX className="w-3 h-3 mr-1" />
                            Inativo
                          </>
                        )}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-sm text-gray-500">
                      {user.createdAt ? new Date(user.createdAt).toLocaleDateString('pt-BR') : '-'}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center justify-end space-x-2">
                        <PermissionGate permission="user_view">
                          <Link
                            to={`/usuarios/ver/${user.id}`}
                            className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                            title="Ver detalhes"
                          >
                            <Eye className="w-4 h-4" />
                          </Link>
                        </PermissionGate>
                        
                        <PermissionGate permission="user_edit">
                          <Link
                            to={`/usuarios/editar/${user.id}`}
                            state={{ userData: user }}
                            className="p-2 text-gray-400 hover:text-yellow-600 transition-colors"
                            title="Editar usuário"
                          >
                            <Edit className="w-4 h-4" />
                          </Link>
                        </PermissionGate>
                        
                        <PermissionGate permission="user_edit">
                          <button
                            onClick={() => handleToggleUserStatus(user.id, user.isActive || false)}
                            className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                            title={user.isActive ? 'Desativar usuário' : 'Ativar usuário'}
                          >
                            {user.isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                          </button>
                        </PermissionGate>
                        
                        <PermissionGate permission="user_delete">
                          <button
                            onClick={() => handleDeleteUser(user.id, user.nome)}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                            title="Remover usuário"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </PermissionGate>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
